import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, LoginCredentials, AuthResponse, User } from '../../types';
import { API_BASE_URL } from '../../lib/utils';
import { clearTerminalLockStorage } from '../../lib/terminalLock';

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('c2_token'),
  refreshToken: localStorage.getItem('c2_refresh_token'),
  isAuthenticated: !!localStorage.getItem('c2_token'),
  isLoading: false,
  error: null,
};

export const loginUser = createAsyncThunk<AuthResponse, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return rejectWithValue(
          (typeof error?.error === 'string' && error.error)
            || (typeof error?.message === 'string' && error.message)
            || 'Login failed',
        );
      }
      const data = await response.json();
      localStorage.setItem('c2_token', data.token);
      return { user: data.user, token: data.token, refreshToken: '' };
    } catch {
      return rejectWithValue('Network error. Please try again.');
    }
  }
);

export const fetchCurrentUser = createAsyncThunk<User>(
  'auth/fetchCurrentUser',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${state.auth.token}` },
      });
      if (!response.ok) {
        return rejectWithValue('Failed to fetch user');
      }
      const data = await response.json();
      return data.user;
    } catch {
      return rejectWithValue('Network error');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('c2_token');
      localStorage.removeItem('c2_refresh_token');
      clearTerminalLockStorage();
    },
    clearError(state) {
      state.error = null;
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        localStorage.removeItem('c2_token');
      });
  },
});

export const { logout, clearError, setUser } = authSlice.actions;
export default authSlice.reducer;
